import { Fragment, forwardRef, useId, type HTMLAttributes, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select } from "./Select";
import { IconButton } from "./IconButton";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <PaginationBar>. Logic
// and CSS ported letter-for-letter from the vendor's compiled bundle and
// its PaginationBar CSS — new .rc-pagination-bar class names. Composes
// the local ./Select and ./IconButton siblings, same as the vendor
// composed its own Select/IconButton. Nav chevrons switched from
// @datasirpi/icons' direction-agnostic ChevronStart/ChevronEnd to
// lucide's ChevronLeft/ChevronRight — this app has no RTL support
// anywhere, so they render identically here, and @datasirpi/icons is
// fully removed from this app now.
export interface PaginationBarProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  totalRows: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: ReadonlyArray<number>;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  renderResultCount?: (from: number, to: number, total: number) => ReactNode;
  rowsPerPageLabel?: string;
  hideResultCount?: boolean;
  hideRowsPerPage?: boolean;
}

function defaultRenderResultCount(from: number, to: number, total: number) {
  return (
    <Fragment>
      Showing <strong>{from}</strong> to <strong>{to}</strong> of <strong>{total}</strong> results
    </Fragment>
  );
}

export const PaginationBar = forwardRef<HTMLDivElement, PaginationBarProps>(function PaginationBar(
  {
    totalRows,
    page,
    rowsPerPage,
    rowsPerPageOptions = [10, 25, 50, 100],
    onPageChange,
    onRowsPerPageChange,
    renderResultCount = defaultRenderResultCount,
    rowsPerPageLabel = "Rows per page",
    hideResultCount = false,
    hideRowsPerPage = false,
    className,
    ...rest
  },
  ref
) {
  const selectId = useId();
  const pageCount = totalRows === 0 ? 1 : Math.ceil(totalRows / rowsPerPage);
  const atFirst = page <= 1;
  const atLast = page >= pageCount;
  const from = totalRows === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage, totalRows);

  function handlePrev() {
    if (!atFirst) onPageChange(page - 1);
  }
  function handleNext() {
    if (!atLast) onPageChange(page + 1);
  }

  return (
    <div ref={ref} className={cn("rc-pagination-bar", className)} {...rest}>
      {!hideResultCount && <span className="rc-pagination-bar__count">{renderResultCount(from, to, totalRows)}</span>}
      <div className="rc-pagination-bar__controls">
        {!hideRowsPerPage && (
          <div className="rc-pagination-bar__rows-per-page">
            <Select
              id={selectId}
              label={rowsPerPageLabel}
              size="sm"
              value={String(rowsPerPage)}
              onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              className="rc-pagination-bar__rows-select"
            >
              {rowsPerPageOptions.map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </Select>
          </div>
        )}
        <IconButton
          size="sm"
          aria-label="Previous page"
          disabled={atFirst}
          icon={<ChevronLeft aria-hidden="true" />}
          onClick={handlePrev}
          className="rc-pagination-bar__nav-btn"
        />
        <span className="rc-pagination-bar__page-indicator" aria-live="polite" aria-atomic="true">
          Page <strong>{page}</strong> of <strong>{pageCount}</strong>
        </span>
        <IconButton
          size="sm"
          aria-label="Next page"
          disabled={atLast}
          icon={<ChevronRight aria-hidden="true" />}
          onClick={handleNext}
          className="rc-pagination-bar__nav-btn"
        />
      </div>
    </div>
  );
});
